import "./Spinner.css";

import  { newElement } from "../Dom";
import Component from "./Component";
import Get, { Post } from "../util/HTTP";
import Card from "./Card";

interface Wheel {
    element: HTMLElement;
    button: HTMLButtonElement;
    items: string[];
    angle: number;
    winner: string | null;
    isSpinning: boolean;
}

interface WinnerResponse {
      race: string;
      class: string;
      url: string;
  }

export default class Spinner extends Component {

    private racesAndClasses: Map<string, string[]> = new Map();
    private spinnerContainer!: HTMLElement;
    private spinnerButtonContainer!: HTMLElement;
    private left!: Wheel;
    private right!: Wheel;

    connectedCallback(): void {
        this.setClassRaces();
        this.spinnerContainer = newElement("div", {id:"spinnerContainer"})
        this.spinnerButtonContainer = newElement("div", {id:"spinnerButtonContainer"})

        this.left = this.createWheel(
            "spinnerLeft",
            "spinnerButtonElemLeft",
            "Choose race",
            [...this.racesAndClasses.keys()],
            
        );

        this.right = this.createWheel(
            "spinnerRight",
            "spinnerButtonElemRight",
            "Choose class",
            [...new Set([...this.racesAndClasses.values()].flat())],
        );

        this.spinnerButtonContainer.appendChild(this.left.button)
        this.spinnerButtonContainer.appendChild(this.right.button)

        this.spinnerContainer.appendChild(this.left.element);
        this.spinnerContainer.appendChild(this.right.element);
        this.spinnerContainer.appendChild(this.spinnerButtonContainer);

        this.replaceChildren(this.spinnerContainer);
    }

    private createWheel(wheelID: string, buttonID: string, label: string, items: string[]): Wheel {
        const wheel: Wheel = {
            element: newElement("div", {id: wheelID}),
            button: newElement("button", {id: buttonID, text: label}),
            items,
            angle: 0,
            winner: null,
            isSpinning: false,
        };

        this.fillWheel(wheel, items);

        wheel.button.addEventListener("click", () => void this.spin(wheel));

        return wheel;
    }

    private async post(): Promise<null | WinnerResponse> {

        const resp = await Post<WinnerResponse>('api/data/', {class: this.right.winner, race: this.left.winner});

        const data = resp.data;

        if(data === null) {
            return null;
        }

        return data
    }

    private async spin(wheel: Wheel): Promise<void> {
        if (wheel.isSpinning) {
            return
        }

        wheel.isSpinning = true;
        this.left.button.disabled = true;
        this.right.button.disabled = true;

        const slice = 360 / wheel.items.length;
        const index = this.randomIndex(wheel.items.length);

        const center = (index + 0.5) * slice;
        const target = this.mod360(360 - center);
        const base = wheel.angle + 5 * 360;
        const newAngle = base + this.mod360(target - base);

        const animation = wheel.element.animate([
            {rotate: newAngle + "deg"}
        ], {
            duration: 4000,
            easing: "cubic-bezier(0.1, 0.8, 0.3, 1)",
            fill: "forwards",
        })

        await animation.finished;

        wheel.element.style.rotate = newAngle + "deg";
        animation.cancel();

        wheel.angle = newAngle;
        wheel.winner = wheel.items[index] ?? null;

        this.applyExclusion(wheel);

        if (this.left.winner !== null && this.right.winner !== null) {
            const race = this.left.winner;
            const playerClass = this.right.winner;

            const data = await this.post();

            if (data !== null) {
                
                new Card()
                    .setUrl(data.url)
                    .setLabel(`${race} ${playerClass}`)
                    .render(this);
            }

            this.resetWheels();
        }

        wheel.isSpinning = false;
        this.left.button.disabled = false;
        this.right.button.disabled = false;
    }

    private applyExclusion(spun: Wheel): void {
        const other = spun === this.left ? this.right : this.left;

        if (spun.winner === null || other.winner !== null) {
            return;
        }

        if (spun === this.left) {
            const classes = this.racesAndClasses.get(spun.winner);
            if (classes !== undefined) {
                this.fillWheel(other, classes);
            }
            return;
        }

        const races: string[] = [];
        for (const [race, classes] of this.racesAndClasses) {
            if (classes.includes(spun.winner)) {
                races.push(race);
            }
        }
        this.fillWheel(other, races);
    }

    private resetWheels(): void {
        this.left.winner = null;
        this.right.winner = null;

        this.fillWheel(this.left, [...this.racesAndClasses.keys()]);
        this.fillWheel(this.right, [...new Set([...this.racesAndClasses.values()].flat())]);
    }

    private randomIndex(count: number): number {
        return Math.floor(Math.random() * count);
    }

    private mod360(value: number): number {
        return ((value % 360) + 360) % 360;
    }

    private fillWheel(wheel: Wheel, items: string[]): void {
        wheel.element.replaceChildren();
        items.forEach((text, index) => {
            const item = newElement("div", {className: "spinnerItem", text});

            item.style.setProperty("--index", index.toString());

            wheel.element.appendChild(item);
        });
        wheel.items = items;

        wheel.element.style.setProperty("--item-count", items.length.toString());

        if (wheel.winner !== null && !items.includes(wheel.winner)) {
            wheel.winner = null;
        }
    }

    private setClassRaces():void {
        this.racesAndClasses.set(
            "Night Elf", ["Druid", "Hunter", "Priest", "Rogue", "Warrior"],
        )
        this.racesAndClasses.set(
            "Dwarf", ["Hunter", "Paladin", "Priest", "Rogue", "Shaman", "Warrior"]
        )
        this.racesAndClasses.set(
            "Human", ["Hunter", "Mage", "Paladin", "Priest", "Rogue", "Warlock", "Warrior"]
        )
        this.racesAndClasses.set(
            "Gnome", ["Mage", "Priest", "Rogue", "Warlock", "Warrior"]
        )
        this.racesAndClasses.set(
            "Orc", ["Hunter", "Mage", "Rogue", "Shaman", "Warlock", "Warrior"]
        )
        this.racesAndClasses.set(
            "Undead", ["Mage", "Paladin", "Priest", "Rogue", "Warlock", "Warrior"]
        )
        this.racesAndClasses.set(
            "Tauren", ["Druid", "Hunter", "Shaman", "Warrior"]
        )
        this.racesAndClasses.set(
            "Troll", ["Hunter", "Mage", "Priest", "Rogue", "Shaman", "Warlock", "Warrior"]
        )
        this.racesAndClasses.set(
            "Skyborne", ["Druid", "Hunter", "Mage", "Rogue", "Shaman", "Warrior"]
        )
    }
}

customElements.define("spinner-view", Spinner);